import React from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { Agency, SubAgency } from 'types/aaaService';
import { FormElementsStyled, FormStyled, SelectStyled, SubtitleStyled, SwissSelectStyled } from './styles';

const ChangeAgencyForm = observer((): JSX.Element => {
  const {
    roleTypeAndAgencyStore: {
      sponsors,
      availableAgencies,
      availableSubAgencies,
      selectedSponsor,
      selectedAgency,
      selectedSubAgency,
      isSingleAgencyWithSubagencies,
      setSelectedSponsor,
      setSelectedAgency,
      setSelectedSubAgency,
    },
  } = useRootStore();

  const convertedSponsors = sponsors.map((sponsor) => toJS(sponsor)).slice();
  const convertedAgencies = availableAgencies.map((agency) => toJS(agency)).slice();
  const convertedSubAgencies = availableSubAgencies.map((subAgency) => toJS(subAgency)).slice();

  const convertedSelectedSponsor = selectedSponsor || toJS(selectedSponsor);
  const convertedSelectedAgency = selectedAgency || toJS(selectedAgency);
  const convertedSelectedSubAgency = selectedSubAgency || toJS(selectedSubAgency);

  const mapAgencyToOption = (agencies: Agency[] | SubAgency[]): OptionType[] =>
    agencies.map((agency) => ({ label: agency.name, value: agency.id }));

  const sponsorOptions = convertedSponsors.map((sponsor) => ({ label: sponsor.name, value: sponsor.id }));
  const agencyOptions = mapAgencyToOption(availableAgencies);
  const subAgencyOptions = mapAgencyToOption(convertedSubAgencies);

  const onSponsorChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const currentSponsor = convertedSponsors.find(({ id }) => id === e.target.value) || null;
    setSelectedSponsor(currentSponsor);
  };

  const onAgencyChange = (value: string): void => {
    const currentAgency = convertedAgencies.find(({ id }) => id === value) || null;
    setSelectedAgency(currentAgency);
  };

  const onSubAgencyChange = (value: string): void => {
    const currentSubAgency = convertedSubAgencies.find(({ id }) => id === value) || null;
    setSelectedSubAgency(currentSubAgency);
  };

  return (
    <FormStyled id="change-agency-form">
      <SubtitleStyled text="Select sponsor and agency" />
      <FormElementsStyled>
        {convertedSponsors.length > 1 && (
          <SelectStyled
            name="sponsor"
            label="Sponsor"
            placeholder="..."
            options={sponsorOptions}
            value={convertedSelectedSponsor?.id || ''}
            onChange={onSponsorChange}
            validationPassed={!!convertedSelectedSponsor?.id}
          />
        )}
        {/* 
          Hide the agency selection if there is only one main agency but multiple subagencies. 
          This prevents users from seeing a single option in the agency dropdown, 
          directing them to choose from the subagency dropdown labeled as "Agency." 
          This design minimizes confusion, as users won't realize they are selecting subagencies directly.
        */}
        {convertedAgencies.length > 0 && !isSingleAgencyWithSubagencies && (
          <SwissSelectStyled
            value={convertedSelectedAgency?.id ? [convertedSelectedAgency?.id] : []}
            withSearch={convertedAgencies.length >= 10}
            onChange={(value: string[]) => onAgencyChange(value[0])}
            options={agencyOptions}
            label="Agency"
            placeholder="..."
            validationPassed={!!convertedSelectedAgency?.id}
            showOptionsCount={20}
            optionHeight={convertedAgencies.length >= 5 ? '30rem' : 'unset'}
          />
        )}
        {convertedSubAgencies.length > 0 && (
          <SwissSelectStyled
            value={convertedSelectedSubAgency?.id ? [convertedSelectedSubAgency?.id] : []}
            withSearch={convertedSubAgencies.length >= 10}
            onChange={(value: string[]) => onSubAgencyChange(value[0])}
            options={subAgencyOptions}
            // The label is changed for better UX, but the functionality remains as a subAgency select.
            label={isSingleAgencyWithSubagencies ? 'Agency' : 'Sub-Agency'}
            placeholder="..."
            showOptionsCount={20}
            optionHeight={convertedSubAgencies.length >= 5 ? '30rem' : 'unset'}
          />
        )}
      </FormElementsStyled>
    </FormStyled>
  );
});

export default ChangeAgencyForm;
